import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Grid,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Checkbox,
  Switch,
  Radio,
  MultiSelect,
  FileInput,
  Text,
  Divider,
} from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconUpload } from '@tabler/icons-react';
import { Button, Card } from '../../ui';
import { FadeIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface FormField {
  key: string;
  label: string;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'email'
    | 'password'
    | 'phone'
    | 'url'
    | 'select'
    | 'multiselect'
    | 'checkbox'
    | 'switch'
    | 'radio'
    | 'date'
    | 'dateRange'
    | 'file'
    | 'custom';
  placeholder?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  options?: { value: string; label: string }[];
  validation?: (value: unknown) => string | null;
  span?: number; // Grid span (1-12)
  section?: string; // Group fields into sections
  render?: (field: FormField, form: ReturnType<typeof useForm>) => ReactNode;
  defaultValue?: unknown;
  min?: number;
  max?: number;
  step?: number;
  accept?: string; // For file inputs
  multiple?: boolean; // For multiselect/file
}

export interface FormSection {
  key: string;
  title: string;
  description?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export interface EntityFormProps<T extends Record<string, unknown>> {
  fields: FormField[];
  sections?: FormSection[];
  initialValues?: Partial<T>;
  onSubmit: (values: T) => void | Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  title?: string;
  description?: string;
  layout?: 'vertical' | 'horizontal';
  columns?: 1 | 2 | 3 | 4;
  showReset?: boolean;
  readonly?: boolean;
  footer?: ReactNode;
  className?: string;
}

export const EntityForm = forwardRef<HTMLFormElement, EntityFormProps<Record<string, unknown>>>(
  (
    {
      fields,
      sections = [],
      initialValues = {},
      onSubmit,
      onCancel,
      loading = false,
      submitLabel = 'Save',
      cancelLabel = 'Cancel',
      title,
      description,
      layout = 'vertical',
      columns = 1,
      showReset = false,
      readonly = false,
      footer,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Build initial values from fields
    const defaultValues = fields.reduce((acc, field) => {
      acc[field.key] = initialValues[field.key] ?? field.defaultValue ?? '';
      return acc;
    }, {} as Record<string, unknown>);

    // Build validation
    const validate = fields.reduce((acc, field) => {
      if (field.validation) {
        acc[field.key] = field.validation;
      } else if (field.required) {
        acc[field.key] = (value: unknown) => {
          if (value === undefined || value === null || value === '') {
            return `${field.label} is required`;
          }
          return null;
        };
      }
      return acc;
    }, {} as Record<string, (value: unknown) => string | null>);

    const form = useForm({
      initialValues: defaultValues,
      validate,
    });

    const handleSubmit = async (values: Record<string, unknown>) => {
      await onSubmit(values);
    };

    const renderField = (field: FormField) => {
      if (field.render) {
        return field.render(field, form);
      }

      const commonProps = {
        label: field.label,
        placeholder: field.placeholder,
        description: field.description,
        required: field.required,
        disabled: field.disabled || readonly,
        ...form.getInputProps(field.key),
      };

      switch (field.type) {
        case 'text':
        case 'email':
        case 'password':
        case 'phone':
        case 'url':
          return (
            <TextInput
              {...commonProps}
              type={field.type === 'phone' ? 'tel' : field.type}
            />
          );

        case 'textarea':
          return <Textarea {...commonProps} minRows={3} autosize />;

        case 'number':
          return (
            <NumberInput
              {...commonProps}
              min={field.min}
              max={field.max}
              step={field.step}
            />
          );

        case 'select':
          return (
            <Select
              {...commonProps}
              data={field.options || []}
              clearable
              searchable
            />
          );

        case 'multiselect':
          return (
            <MultiSelect
              {...commonProps}
              data={field.options || []}
              clearable
              searchable
            />
          );

        case 'checkbox':
          return (
            <Checkbox
              label={field.label}
              description={field.description}
              disabled={field.disabled || readonly}
              {...form.getInputProps(field.key, { type: 'checkbox' })}
            />
          );

        case 'switch':
          return (
            <Switch
              label={field.label}
              description={field.description}
              disabled={field.disabled || readonly}
              {...form.getInputProps(field.key, { type: 'checkbox' })}
            />
          );

        case 'radio':
          return (
            <Radio.Group
              label={field.label}
              description={field.description}
              required={field.required}
              {...form.getInputProps(field.key)}
            >
              <Stack gap="xs" mt="xs">
                {field.options?.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    disabled={field.disabled || readonly}
                  />
                ))}
              </Stack>
            </Radio.Group>
          );

        case 'date':
          return <DateInput {...commonProps} clearable />;

        case 'dateRange':
          return <DatePickerInput {...commonProps} type="range" clearable />;

        case 'file':
          return (
            <FileInput
              {...commonProps}
              accept={field.accept}
              multiple={field.multiple}
              leftSection={<IconUpload size={16} />}
            />
          );

        default:
          return <TextInput {...commonProps} />;
      }
    };

    const getFieldsForSection = (sectionKey?: string) =>
      fields.filter((field) => field.section === sectionKey);

    const fieldsWithoutSection = fields.filter((field) => !field.section);

    const renderFieldsGrid = (fieldList: FormField[]) => (
      <Grid>
        {fieldList.map((field) => (
          <Grid.Col key={field.key} span={{ base: 12, sm: field.span || 12 / columns }}>
            {renderField(field)}
          </Grid.Col>
        ))}
      </Grid>
    );

    return (
      <FadeIn>
        <Card className={className}>
          <form ref={ref} onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="lg">
              {/* Header */}
              {(title || description) && (
                <Box>
                  {title && <Text size="lg" fw={600} style={accessibilityStyles}>{title}</Text>}
                  {description && <Text size="sm" c="dimmed" style={accessibilityStyles}>{description}</Text>}
                </Box>
              )}

              {/* Fields without section */}
              {fieldsWithoutSection.length > 0 && renderFieldsGrid(fieldsWithoutSection)}

              {/* Sectioned fields */}
              {sections.map((section) => {
                const sectionFields = getFieldsForSection(section.key);
                if (sectionFields.length === 0) return null;

                return (
                  <Box key={section.key}>
                    <Divider
                      label={
                        <Box>
                          <Text fw={500} style={accessibilityStyles}>{section.title}</Text>
                          {section.description && (
                            <Text size="xs" c="dimmed" style={accessibilityStyles}>{section.description}</Text>
                          )}
                        </Box>
                      }
                      labelPosition="left"
                      mb="md"
                    />
                    {renderFieldsGrid(sectionFields)}
                  </Box>
                );
              })}

              {/* Footer */}
              {footer}

              {/* Actions */}
              <Group justify="flex-end" gap="sm">
                {showReset && (
                  <Button
                    variant="subtle"
                    onClick={() => form.reset()}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                )}
                {onCancel && (
                  <Button variant="default" onClick={onCancel} disabled={loading}>
                    {cancelLabel}
                  </Button>
                )}
                <Button type="submit" loading={loading} disabled={readonly}>
                  {submitLabel}
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      </FadeIn>
    );
  }
);

EntityForm.displayName = 'EntityForm';

export default EntityForm;
